/**
 * Google Maps JavaScript API loader.
 *
 * Injects the Maps JS script exactly once per browser session, caches the
 * in-flight Promise so concurrent callers share it, and resolves to the
 * `google` namespace. Rejects on missing API key, 10-second timeout, or
 * script `error` event.
 */

// Minimal typing — `@types/google.maps` is not installed. Consumers can cast
// the resolved value to the full google namespace once types are available.
// We expose the resolution value as `unknown` (aliased to `GoogleMapsNamespace`)
// here so the public signature is honest without requiring a global type.
declare global {
  interface Window {
    google?: { maps?: { places?: unknown } };
  }
}

export type GoogleMapsNamespace = unknown;

export interface LoadMapsOptions {
  apiKey: string;
  language?: 'ko' | 'en';
  region?: string;
}

export class MapsLoaderError extends Error {
  constructor(
    message: string,
    public readonly code: 'MISSING_KEY' | 'TIMEOUT' | 'SCRIPT_ERROR',
  ) {
    super(message);
    this.name = 'MapsLoaderError';
  }
}

const LOAD_TIMEOUT_MS = 10_000;

let cachedPromise: Promise<GoogleMapsNamespace> | null = null;

export function loadGoogleMaps(opts: LoadMapsOptions): Promise<GoogleMapsNamespace> {
  if (!opts.apiKey) {
    return Promise.reject(
      new MapsLoaderError(
        'Google Maps API key is missing. Set REACT_APP_GOOGLE_MAPS_API_KEY.',
        'MISSING_KEY',
      ),
    );
  }

  if (window.google?.maps?.places) {
    return Promise.resolve(window.google as GoogleMapsNamespace);
  }

  if (cachedPromise) {
    return cachedPromise;
  }

  cachedPromise = new Promise<GoogleMapsNamespace>((resolve, reject) => {
    const params = new URLSearchParams({
      key: opts.apiKey,
      libraries: 'places',
      loading: 'async',
      v: 'weekly',
    });
    if (opts.language) params.set('language', opts.language);
    if (opts.region) params.set('region', opts.region);

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
    script.async = true;
    script.defer = true;

    const timeoutId = window.setTimeout(() => {
      cleanup();
      cachedPromise = null;
      reject(
        new MapsLoaderError(
          `Google Maps failed to load within ${LOAD_TIMEOUT_MS}ms.`,
          'TIMEOUT',
        ),
      );
    }, LOAD_TIMEOUT_MS);

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      script.onload = null;
      script.onerror = null;
    };

    script.onload = () => {
      // The loader script we just injected kicks off *more* async scripts
      // (places.js, main.js). `google.maps.places` isn't guaranteed to be
      // ready the moment `onload` fires, so poll until it is — or until the
      // overall timeout above trips.
      const pollInterval = 50;
      const pollStart = Date.now();
      const poll = () => {
        if (window.google?.maps?.places) {
          cleanup();
          resolve(window.google as GoogleMapsNamespace);
          return;
        }
        if (Date.now() - pollStart >= LOAD_TIMEOUT_MS) {
          cleanup();
          cachedPromise = null;
          reject(
            new MapsLoaderError(
              'Google Maps script loaded but `google.maps.places` is unavailable.',
              'SCRIPT_ERROR',
            ),
          );
          return;
        }
        window.setTimeout(poll, pollInterval);
      };
      poll();
    };

    script.onerror = () => {
      cleanup();
      cachedPromise = null;
      reject(
        new MapsLoaderError('Google Maps script failed to load.', 'SCRIPT_ERROR'),
      );
    };

    document.head.appendChild(script);
  });

  return cachedPromise;
}

// Test-only hook to reset the cached Promise between tests.
export function __resetMapsLoaderForTests(): void {
  cachedPromise = null;
}
